import {
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT, SLIDE_FEATHER_ICON} from '#/lib/slideTokens'
import {type SlideEditorProps} from '#/components/capsules/play/types'
import {StudioTextField} from '#/components/studio/StudioTextField'
import {readableAccent, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {SLIDE_TYPE_META} from '#/types/capsule'
import {type MatchPair} from '#/types/capsule'

function uid() {
  return Math.random().toString(36).slice(2)
}

export function MatchingSlideEditor({
  slide,
  onChange,
  primaryColor: _primaryColor,
  onPreview,
}: SlideEditorProps) {
  const ss = useStyles()
  const chrome = useStudioChrome()
  const {t: l} = useLingui()
  const accent = SLIDE_ACCENT.matching
  // Ink, not fill: the taxonomy's colour tuned to read on the studio surface it
  // is written on (see `readableAccent`). Tints stay on the raw hue.
  const accentInk = readableAccent(accent, chrome)
  const meta = SLIDE_TYPE_META.matching
  const slideIcon = SLIDE_FEATHER_ICON.matching ?? 'help-circle'
  const pairs: MatchPair[] = slide.pairs ?? []

  const updatePair = (id: string, patch: Partial<MatchPair>) => {
    onChange({pairs: pairs.map(p => (p.id === id ? {...p, ...patch} : p))})
  }

  const addPair = () => {
    onChange({pairs: [...pairs, {id: uid(), left: '', right: ''}]})
  }

  const removePair = (id: string) => {
    if (pairs.length <= 2) return
    onChange({pairs: pairs.filter(p => p.id !== id)})
  }

  return (
    <ScrollView
      style={ss.root}
      contentContainerStyle={ss.inner}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}>
      {/* Type badge row */}
      {/* Host-provided header (e.g. FlashBook studio) replaces this row */}
      <View style={[ss.topRow, onPreview ? null : {display: 'none'}]}>
        <View
          style={[
            ss.typeBadge,
            {
              backgroundColor: accent + '22',
              borderColor: accent + '44',
            },
          ]}>
          <Feather name={slideIcon} size={12} color={accentInk} />
          <Text style={[ss.typeBadgeText, {color: accentInk}]}>
            {meta?.label ?? slide.type}
          </Text>
        </View>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={onPreview}
          hitSlop={6}
          style={[
            ss.previewBtn,
            {
              backgroundColor: accent + '18',
              borderColor: accent + '55',
            },
          ]}>
          <Feather name="play-circle" size={13} color={accentInk} />
          <Text
            style={[ss.previewBtnText, {color: accentInk}]}>{l`Preview`}</Text>
        </TouchableOpacity>
      </View>

      {/* Question */}
      <StudioTextField
        accessibilityLabel="Text input field"
        accessibilityHint={undefined}
        style={ss.question}
        value={slide.question ?? ''}
        onChangeText={v => onChange({question: v})}
        placeholder={l`Écris ta question ici…`}
        placeholderTextColor={chrome.textFaint}
      />

      {/* Pairs */}
      <Text style={ss.sectionLabel}>{l`PAIRES À ASSOCIER`.toUpperCase()}</Text>
      {pairs.map((pair, i) => (
        <View key={pair.id} style={ss.pairRow}>
          <View style={ss.leftCard}>
            <TextInput
              accessibilityLabel="Text input field"
              accessibilityHint={undefined}
              style={ss.leftInput}
              value={pair.left}
              onChangeText={v => updatePair(pair.id, {left: v})}
              placeholder={l`Terme ${i + 1}`}
              placeholderTextColor={chrome.textFaint}
            />
          </View>
          <Feather name="arrow-right" size={14} color={chrome.textFaint} />
          <View style={[ss.rightCard, {borderColor: accent + '44'}]}>
            <TextInput
              accessibilityLabel="Text input field"
              accessibilityHint={undefined}
              style={[ss.rightInput, {color: accentInk}]}
              value={pair.right}
              onChangeText={v => updatePair(pair.id, {right: v})}
              placeholder={l`Correspond. ${i + 1}`}
              placeholderTextColor={accent + '44'}
            />
          </View>
          {pairs.length > 2 && (
            <TouchableOpacity
              accessibilityRole="button"
              onPress={() => removePair(pair.id)}>
              <Feather name="trash-2" size={14} color={chrome.textFaint} />
            </TouchableOpacity>
          )}
        </View>
      ))}
      <TouchableOpacity
        accessibilityRole="button"
        onPress={addPair}
        style={ss.addBtn}>
        <Feather name="plus" size={14} color={chrome.textFaint} />
        <Text style={ss.addTxt}>{l`Add a pair`}</Text>
      </TouchableOpacity>

      {/* Explanation */}
      <View style={ss.explanationWrap}>
        <Text style={ss.explanationLabel}>
          {l`EXPLICATION (optionnelle)`.toUpperCase()}
        </Text>
        <View style={ss.explanationCard}>
          <StudioTextField
            accessibilityLabel="Text input field"
            accessibilityHint={undefined}
            style={ss.explanationInput}
            value={slide.explanation ?? ''}
            onChangeText={v => onChange({explanation: v})}
            placeholder={l`Explication affichée après la réponse…`}
            placeholderTextColor={chrome.textFaint}
            textAlignVertical="top"
          />
        </View>
      </View>
    </ScrollView>
  )
}

const makeStyles = (chrome: StudioChrome) =>
  StyleSheet.create({
    root: {flex: 1, backgroundColor: chrome.frame},
    inner: {padding: 20, paddingBottom: 40, gap: 16},
    topRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    typeBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      alignSelf: 'flex-start',
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    typeBadgeText: {fontSize: 11, fontWeight: '700'},
    previewBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    previewBtnText: {fontSize: 11, fontWeight: '700'},
    question: {
      color: chrome.text,
      fontSize: 20,
      fontWeight: '700',
      lineHeight: 28,
      paddingVertical: 0,
    },
    sectionLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.9,
    },
    pairRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    leftCard: {
      flex: 1,
      backgroundColor: chrome.fill,
      borderRadius: 12,
      borderWidth: 1.5,
      borderColor: chrome.borderStrong,
      paddingHorizontal: 12,
      paddingVertical: 9,
    },
    leftInput: {color: chrome.text, fontSize: 14, paddingVertical: 0},
    rightCard: {
      flex: 1,
      backgroundColor: chrome.fill,
      borderRadius: 12,
      borderWidth: 1.5,
      paddingHorizontal: 12,
      paddingVertical: 9,
    },
    rightInput: {fontSize: 14, paddingVertical: 0},
    addBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 10,
      paddingHorizontal: 14,
      borderRadius: 12,
      borderWidth: 1.5,
      borderStyle: 'dashed',
      borderColor: chrome.borderStrong,
    },
    addTxt: {color: chrome.textFaint, fontSize: 13},
    explanationWrap: {gap: 6},
    explanationLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.9,
    },
    explanationCard: {
      backgroundColor: chrome.fill,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    explanationInput: {
      color: chrome.textMuted,
      fontSize: 13,
      lineHeight: 18,
      paddingVertical: 0,
    },
  })

/** The editor's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
